// src/pages/TrainingDetailPage.jsx
import React from "react";
import { useParams, Link } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Chip,
  Stack,
  Button,
  CircularProgress,
  Breadcrumbs,
  Link as MuiLink,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, Package } from "lucide-react";
import DOMPurify from "dompurify";

import { getTrainingBySlug } from "../api/trainings";
import PageShell from "../components/layout/PageShell";
import ContactSection from "../components/shared/ContactSection";
import { TrainingThumb, formatPrice } from "../features/training/trainingShared";

function FormattedDescription({ html }) {
  if (!html) return null;

  const clean = DOMPurify.sanitize(
    html.replace(/&nbsp;/gi, " ").replace(/\u00A0/g, " "),
    {
      ALLOWED_TAGS: [
        "p", "br", "strong", "b", "em", "i",
        "u", "ul", "ol", "li", "h1", "h2",
        "h3", "h4", "a",
      ],
      ALLOWED_ATTR: ["href", "target", "rel"],
    }
  );

  return (
    <Box
      sx={{
        fontSize: 15,
        color: "inherit",
        lineHeight: 1.9,
        whiteSpace: "normal",
        overflowWrap: "break-word",
        wordBreak: "normal",
        textAlign: "left",
        "& p": {
          margin: "0 0 18px",
          textAlign: "left",
          lineHeight: 1.9,
        },
        "& p:last-child": {
          marginBottom: 0,
        },
        "& h1, & h2, & h3, & h4": {
          color: "#0B1530",
          fontWeight: 700,
          lineHeight: 1.4,
          margin: "24px 0 12px",
        },
        "& ul, & ol": {
          paddingLeft: "24px",
          margin: "12px 0 18px",
        },
        "& li": {
          marginBottom: "8px",
        },
        "& strong, & b": {
          fontWeight: 700,
        },
        "& a": {
          color: "#2563EB",
          textDecoration: "underline",
        },
      }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}

export default function TrainingDetailPage() {
  const { slug } = useParams();

  const {
    data: training,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["training", slug],
    queryFn: () =>
      getTrainingBySlug(slug).then((r) => r.data?.data ?? r.data),
    enabled: Boolean(slug),
  });

  const enrollHref = training
    ? `/enroll?training=${encodeURIComponent(
        training.slug || slug
      )}&title=${encodeURIComponent(training.title || "")}`
    : `/enroll?training=${encodeURIComponent(slug || "")}`;

  const priceLabel = training
    ? formatPrice(training.price, training.currency)
    : null;

  const description =
    training?.short_description ||
    training?.shortDescription ||
    training?.excerpt ||
    training?.description ||
    "";

  const image =
    training?.imageUrl ||
    training?.image_url ||
    training?.image ||
    training?.thumbnail_url ||
    training?.thumbnail ||
    training?.banner ||
    training?.cover_image ||
    training?.featured_image ||
    "";

  if (isLoading) {
    return (
      <PageShell>
        <Box
          sx={{
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (isError || !training) {
    return (
      <PageShell>
        <Container maxWidth="lg" sx={{ py: 10 }}>
          <Typography variant="h5" fontWeight={700}>
            Training not found
          </Typography>

          <Typography sx={{ mt: 1, color: "#64748B" }}>
            We could not load this training. Please try again.
          </Typography>

          <Button
            component={Link}
            to="/trainings"
            startIcon={<ArrowLeft size={18} />}
            sx={{ mt: 3 }}
          >
            Back to trainings
          </Button>
        </Container>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box sx={{ bgcolor: "#F5F8FC", minHeight: "100vh", pb: 8 }}>

        {/* Blue gradient hero section */}
        <Box
          sx={{
            background:
              "linear-gradient(115deg, #081A35 0%, #12396B 55%, #2459B5 100%)",
            color: "#FFFFFF",
            pt: { xs: 4, md: 5 },
            pb: { xs: 12, md: 16 },
          }}
        >
          <Container maxWidth="lg">
            <Breadcrumbs
              separator="/"
              sx={{
                mb: 3,
                color: "rgba(255,255,255,0.8)",
                "& .MuiBreadcrumbs-separator": {
                  color: "rgba(255,255,255,0.6)",
                },
              }}
            >
              <MuiLink
                component={Link}
                to="/"
                underline="hover"
                color="inherit"
                sx={{ fontSize: 14 }}
              >
                Home
              </MuiLink>

              <MuiLink
                component={Link}
                to="/trainings"
                underline="hover"
                color="inherit"
                sx={{ fontSize: 14 }}
              >
                Training
              </MuiLink>

              <Typography
                sx={{
                  color: "#FFFFFF",
                  fontSize: 14,
                  fontWeight: 600,
                }}
              >
                {training.title}
              </Typography>
            </Breadcrumbs>

            <Stack
              direction="row"
              spacing={1}
              sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}
            >
              <Chip
                label={training.is_bestseller ? "Bestseller" : "Training"}
                size="small"
                sx={{
                  bgcolor: "#FFFFFF",
                  color: "#17396C",
                  fontWeight: 700,
                }}
              />

              <Chip
                label={training.training_type || "Classroom"}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.15)",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  textTransform: "capitalize",
                }}
              />
            </Stack>

            <Typography
              component="h1"
              sx={{
                fontSize: { xs: 32, sm: 42, md: 52 },
                lineHeight: 1.15,
                letterSpacing: "-0.8px",
                fontWeight: 750,
                mb: 2,
                maxWidth: 1000,
              }}
            >
              {training.title}
            </Typography>

            {description && (
              <Box
                sx={{
                  maxWidth: 900,
                  color: "rgba(255,255,255,0.9)",
                  fontSize: 16,
                  lineHeight: 1.9,
                  "& p": { color: "inherit" },
                  "& h1, & h2, & h3, & h4": {
                    color: "#FFFFFF",
                  },
                  "& strong, & b": {
                    color: "#FFFFFF",
                  },
                }}
              >
                <FormattedDescription html={description} />
              </Box>
            )}
          </Container>
        </Box>

        {/* Main content overlapping the hero section */}
        <Container
          maxWidth="lg"
          sx={{
            mt: { xs: -6, md: -8 },
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                md: "minmax(0, 1.9fr) minmax(300px, 1fr)",
              },
              gap: 3,
              alignItems: "start",
            }}
          >

            {/* Left column: image and training description */}
            <Box
              sx={{
                bgcolor: "#FFFFFF",
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid #E2E8F0",
                boxShadow: "0 18px 45px rgba(15, 35, 70, 0.12)",
                minWidth: 0,
              }}
            >
              {image ? (
                <Box
                  component="img"
                  src={image}
                  alt={training.title}
                  onError={(e) => {
                    console.error("Failed to load course image:", image);
                  }}
                  sx={{
                    width: "100%",
                    height: { xs: 230, sm: 360, md: 395 },
                    objectFit: "cover",
                    display: "block",
                    bgcolor: "#FFFFFF",
                  }}
                    />
                  ) : (
                    <Box
                      sx={{
                        minHeight: { xs: 230, sm: 360, md: 395 },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#FFFFFF",
                      }}
                    >
                      <TrainingThumb training={training} />
                    </Box>
                  )}

              <Box sx={{ p: { xs: 3, md: 4 } }}>
                <Typography
                  component="h2"
                  sx={{
                    fontSize: 23,
                    fontWeight: 750,
                    color: "#0B1530",
                    mb: 2,
                    pl: 1.5,
                    borderLeft: "4px solid #2563EB",
                  }}
                >
                  About this training
                </Typography>

                <FormattedDescription
                  html={
                    training.long_description ||
                    training.longDescription ||
                    training.description ||
                    description
                  }
                />

                {training.objectives && (
                  <Box sx={{ mt: 4 }}>
                    <Typography
                      component="h2"
                      sx={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: "#0B1530",
                        mb: 2,
                      }}
                    >
                      Training objectives
                    </Typography>

                    <FormattedDescription html={training.objectives} />
                  </Box>
                )}
              </Box>
            </Box>

            {/* Right column: action card and training details */}
            <Stack
              spacing={2.5}
              sx={{
                minWidth: 0,
                position: { xs: "static", md: "sticky" },
                top: { xs: "auto", md: "100px" },
                alignSelf: "flex-start",
              }}
            >

              {/* Blue action card */}
              <Box
                sx={{
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: 3,
                  p: { xs: 3, md: 3.5 },
                  color: "#FFFFFF",
                  background:
                    "linear-gradient(135deg, #17469C 0%, #2864EA 100%)",
                  boxShadow: "0 18px 40px rgba(37, 99, 235, 0.22)",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    width: 130,
                    height: 130,
                    borderRadius: "0 0 0 100%",
                    right: 0,
                    top: 0,
                    bgcolor: "rgba(255,255,255,0.12)",
                  },
                }}
              >
                <Box sx={{ position: "relative", zIndex: 1 }}>
                  <Package size={30} />

                  <Typography
                    component="h2"
                    sx={{
                      mt: 2.5,
                      mb: 1.5,
                      fontSize: { xs: 22, md: 25 },
                      fontWeight: 750,
                      lineHeight: 1.3,
                    }}
                  >
                    Interested in this training?
                  </Typography>

                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.9)",
                      lineHeight: 1.8,
                      fontSize: 14.5,
                    }}
                  >
                    Contact our team to learn more about this training and
                    find the right solution for your requirements.
                  </Typography>

                  <Button
                    component={Link}
                    to={enrollHref}
                    fullWidth
                    variant="contained"
                    sx={{
                      mt: 3,
                      py: 1.5,
                      bgcolor: "#FFFFFF",
                      color: "#10264A",
                      fontWeight: 700,
                      borderRadius: 2.5,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "#EFF6FF",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Enroll Now
                  </Button>

                  <Button
                    component={Link}
                    to="/trainings"
                    fullWidth
                    sx={{
                      mt: 1,
                      color: "#FFFFFF",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.1)",
                      },
                    }}
                  >
                    Explore other trainings
                  </Button>
                </Box>
              </Box>

              {/* White training details card */}
              <Box
                sx={{
                  bgcolor: "#FFFFFF",
                  border: "1px solid #DCE5F1",
                  borderRadius: 3,
                  p: { xs: 3, md: 3.5 },
                  boxShadow: "0 12px 30px rgba(15, 35, 70, 0.06)",
                }}
              >
                <Typography
                  component="h2"
                  sx={{
                    fontSize: 21,
                    fontWeight: 750,
                    color: "#0B1530",
                    mb: 3,
                  }}
                >
                  Training details
                </Typography>

                {[
                  {
                    label: "Category",
                    value:
                      training.category?.name ||
                      training.category_name ||
                      "Training",
                  },
                  {
                    label: "Training type",
                    value:
                      training.training_type || training.mode || "Classroom",
                  },
                  {
                    label: "Duration",
                    value: training.duration || "Contact us",
                  },
                  {
                    label: "Availability",
                    value: training.is_bestseller
                      ? "Bestseller"
                      : "Available",
                  },
                ].map((item, index, array) => (
                  <Box
                    key={item.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                      py: 1.6,
                      borderBottom:
                        index === array.length - 1
                          ? "none"
                          : "1px dashed #DCE5F1",
                    }}
                  >
                    <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                      {item.label}
                    </Typography>

                    <Chip
                      label={item.value}
                      size="small"
                      sx={{
                        maxWidth: "65%",
                        bgcolor: "#EDF4FF",
                        color: "#2563EB",
                        fontWeight: 650,
                        borderRadius: 2,
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  </Box>
                ))}

                {priceLabel && (
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop: "1px solid #E2E8F0",
                    }}
                  >
                    <Typography sx={{ color: "#64748B", fontSize: 14 }}>
                      Training fee
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.5,
                        color: "#0B1530",
                        fontSize: 25,
                        fontWeight: 750,
                      }}
                    >
                      {priceLabel}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Stack>
          </Box>
        </Container>

        {/* Contact section */}
        <Box sx={{ mt: 8 }}>
          <ContactSection />
        </Box>
      </Box>
    </PageShell>
  );
}